import React, { useEffect, useState } from "react";
import { DollarSign, TrendingUp, Building2, Layers, Loader2, Info, FileDown, AlertTriangle } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { sr } from "../../lib/sr";

const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

const th = { padding: "6px 8px" };
const thR = { padding: "6px 8px", textAlign: "right" };
const td = { padding: "6px 8px" };
const tdR = { padding: "6px 8px", textAlign: "right" };

export default function MarginReport({ embedded = false }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [view, setView] = useState("planned");
  const [threshold, setThreshold] = useState("");
  const [saving, setSaving] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const load = (thr) => sr.adminMarginReport(thr).then((d) => {
    setData(d); setThreshold(String(d.threshold_pct));
  }).catch(() => setErr(true));

  useEffect(() => { load(); }, []);

  const saveThreshold = async () => {
    const v = Number(threshold);
    if (!Number.isFinite(v) || v < 0) return;
    setSaving(true);
    try { await sr.adminMarginThreshold(v); await load(v); } finally { setSaving(false); }
  };

  const exportPdf = async () => {
    setPdfBusy(true);
    try {
      const blob = await sr.adminMarginReportPdf(view, data ? data.threshold_pct : undefined);
      const stamp = new Date().toISOString().slice(0, 10);
      downloadBlob(blob, `margin_report_${view}_${stamp}.pdf`);
    } finally { setPdfBusy(false); }
  };

  if (err) return (
    <div className="sr-card" data-testid="margin-report-error" style={{ marginTop: embedded ? 14 : 0 }}>
      <p className="sr-muted">This report is available to admins only.</p>
    </div>
  );
  if (!data) return <div className="sr-empty" data-testid="margin-report-loading"><Loader2 className="sr-spin" /></div>;

  const ds = view === "live"
    ? data.live
    : { totals: data.totals, clients: data.clients, by_channel: data.by_channel, has_data: (data.clients || []).length > 0 };
  const t = ds.totals || {};
  const flaggedCount = view === "live" ? (data.low_margin?.live_count || 0) : (data.low_margin?.planned_count || 0);

  return (
    <div data-testid="margin-report" style={{ maxWidth: embedded ? "none" : 1000, margin: embedded ? 0 : "0 auto", padding: embedded ? 0 : "8px 4px 40px" }}>
      {!embedded && (
        <>
          <h1 style={{ color: "var(--navy)", fontSize: 26, marginBottom: 4, display: "inline-flex", alignItems: "center", gap: 8 }}><DollarSign size={22} /> Margin Report</h1>
          <p className="sr-muted" style={{ marginBottom: 16 }}>Admin-only. Our margin across every client — proposed plans and live campaigns. This is never shown in the client-facing plan designer.</p>
        </>
      )}

      {/* Controls */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", justifyContent: "space-between", marginTop: embedded ? 6 : 0, marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 6 }} data-testid="margin-view-tabs">
          <button className={`sr-btn sr-btn-sm ${view === "planned" ? "sr-btn-primary" : "sr-btn-ghost"}`} onClick={() => setView("planned")} data-testid="margin-view-planned">Proposed plans</button>
          <button className={`sr-btn sr-btn-sm ${view === "live" ? "sr-btn-primary" : "sr-btn-ghost"}`} onClick={() => setView("live")} data-testid="margin-view-live">Live spend</button>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span className="sr-muted" style={{ fontSize: 12 }}>Low-margin below</span>
          <input className="sr-input" type="number" min="0" max="100" value={threshold} onChange={(e) => setThreshold(e.target.value)} data-testid="margin-threshold-input" style={{ width: 70, padding: "6px 8px" }} />
          <span className="sr-muted" style={{ fontSize: 12 }}>%</span>
          <button className="sr-btn sr-btn-sm sr-btn-ghost" onClick={saveThreshold} disabled={saving} data-testid="margin-threshold-save">{saving ? <Loader2 size={13} className="sr-spin" /> : "Apply"}</button>
          <button className="sr-btn sr-btn-sm" onClick={exportPdf} disabled={pdfBusy} data-testid="margin-export-pdf" style={{ background: "#fff" }}>{pdfBusy ? <Loader2 size={13} className="sr-spin" /> : <><FileDown size={13} /> PDF</>}</button>
        </div>
      </div>

      {flaggedCount > 0 && (
        <div className="sr-card" data-testid="margin-low-alert" style={{ borderColor: "#FDA29B", background: "#FFFBFA", marginBottom: 14, display: "flex", alignItems: "center", gap: 8, padding: 12 }}>
          <AlertTriangle size={16} color="#B42318" />
          <span style={{ fontSize: 13, color: "#B42318" }}><strong>{flaggedCount}</strong> client{flaggedCount === 1 ? "" : "s"} below the {data.threshold_pct}% markup threshold in this view.</span>
        </div>
      )}

      {/* Totals */}
      <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))" }} data-testid="margin-totals">
        <Stat label="Total client budget" value={usd(t.client_spend)} icon={Building2} />
        <Stat label="Provider media cost" value={usd(t.provider_media)} icon={Layers} />
        <Stat label="Our margin" value={usd(t.margin)} icon={DollarSign} accent />
        <Stat label="Blended markup" value={`${t.blended_markup_pct || 0}%`} icon={TrendingUp} />
      </div>

      <MarginTrend />

      {view === "live" && !ds.has_data ? (
        <div className="sr-card" style={{ marginTop: 18 }} data-testid="margin-live-empty">
          <p className="sr-muted" style={{ fontSize: 14 }}>No live campaigns yet. Realized margin appears here once campaigns go live.</p>
        </div>
      ) : (
        <>
          {/* By channel */}
          <div className="sr-card" style={{ marginTop: 18 }} data-testid="margin-by-channel">
            <div className="sr-eyebrow" style={{ marginBottom: 10 }}>Margin by channel</div>
            {(ds.by_channel || []).length === 0 ? (
              <p className="sr-muted" style={{ fontSize: 14 }}>No data yet.</p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead><tr style={{ textAlign: "left", color: "var(--muted)" }}>
                    <th style={th}>Channel</th><th style={thR}>Client budget</th><th style={thR}>Provider media</th><th style={thR}>Margin</th><th style={thR}>Markup</th></tr></thead>
                  <tbody>
                    {ds.by_channel.map((c) => (
                      <tr key={c.channel} style={{ borderTop: "1px solid var(--border)" }} data-testid={`margin-channel-${c.channel}`}>
                        <td style={td}>{c.label}</td>
                        <td style={tdR}>{usd(c.client_spend)}</td>
                        <td style={tdR}>{usd(c.provider_media)}</td>
                        <td style={{ ...tdR, fontWeight: 700, color: "#067647" }}>{usd(c.margin)}</td>
                        <td style={tdR}><Markup c={c} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* By client */}
          <div className="sr-card" style={{ marginTop: 18 }} data-testid="margin-by-client">
            <div className="sr-eyebrow" style={{ marginBottom: 10 }}>Margin by client</div>
            {(ds.clients || []).length === 0 ? (
              <p className="sr-muted" style={{ fontSize: 14 }}>No clients with {view === "live" ? "live campaigns" : "a saved media plan"} yet.</p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead><tr style={{ textAlign: "left", color: "var(--muted)" }}>
                    <th style={th}>Client</th><th style={th}>{view === "live" ? "Campaigns" : "Plan"}</th><th style={thR}>Client budget</th><th style={thR}>Provider media</th><th style={thR}>Margin</th><th style={thR}>Markup</th></tr></thead>
                  <tbody>
                    {ds.clients.map((c) => (
                      <tr key={c.tenant_id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`margin-client-${c.tenant_id}`}>
                        <td style={{ ...td, fontWeight: 600, color: "var(--navy)" }}>{c.client}</td>
                        <td style={td}>{c.plan_name}</td>
                        <td style={tdR}>{usd(c.client_spend)}</td>
                        <td style={tdR}>{usd(c.provider_media)}</td>
                        <td style={{ ...tdR, fontWeight: 700, color: "#067647" }} data-testid={`margin-client-amt-${c.tenant_id}`}>{usd(c.margin)}</td>
                        <td style={tdR}><Markup c={c} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      <p className="sr-muted" style={{ fontSize: 12, marginTop: 12, display: "flex", gap: 5 }}><Info size={12} style={{ flexShrink: 0, marginTop: 2 }} /> {data.note}</p>
    </div>
  );
}

function Markup({ c }) {
  if (c.below_threshold) {
    return <span style={{ color: "#B42318", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 3, justifyContent: "flex-end" }}><AlertTriangle size={12} /> {c.markup_pct}%</span>;
  }
  return <span>{c.markup_pct}%</span>;
}

function Stat({ label, value, icon: Icon, accent }) {
  return (
    <div className="sr-card" style={{ padding: 14, border: accent ? "1px solid #067647" : "1px solid var(--border)", background: accent ? "#F2FBF5" : "var(--card,#fff)" }}>
      <div className="sr-muted" style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 5 }}><Icon size={12} /> {label}</div>
      <div className="sr-stat-val" style={{ marginTop: 6, fontSize: 22, color: accent ? "#067647" : "var(--navy)" }}>{value}</div>
    </div>
  );
}

function MarginTrend() {
  const [trend, setTrend] = useState(null);
  useEffect(() => { sr.adminMarginTrend(90).then(setTrend).catch(() => setTrend({ series: [] })); }, []);
  const series = trend?.series || [];
  if (!trend || series.length === 0) return null;
  const shortDate = (d) => String(d).slice(5).replace("-", "/");
  return (
    <div className="sr-card" style={{ marginTop: 18 }} data-testid="margin-trend">
      <div className="sr-eyebrow" style={{ marginBottom: 2, display: "flex", alignItems: "center", gap: 6 }}><TrendingUp size={13} /> Margin trend — live, last 90 days</div>
      <p className="sr-muted" style={{ fontSize: 11.5, marginBottom: 8 }}>{trend.note}</p>
      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={series} margin={{ top: 6, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="mtg" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#7A5AF8" stopOpacity={0.35} />
              <stop offset="100%" stopColor="#7A5AF8" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F4" vertical={false} />
          <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 10, fill: "#667085" }} minTickGap={28} />
          <YAxis tickFormatter={(v) => `$${Math.round(v / 1000)}k`} tick={{ fontSize: 10, fill: "#667085" }} width={44} />
          <Tooltip formatter={(v) => [usd(v), "Live margin"]} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
          <Area type="monotone" dataKey="live_margin" name="Live margin" stroke="#7A5AF8" strokeWidth={2} fill="url(#mtg)" dot={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
